import { SolutionScenario } from '@/types/scenarios/solution';
import { Home, Phone, Calendar, Wrench, HelpCircle, Package, FileText, Thermometer, Lightbulb, Shield } from 'lucide-react';

export const homeBuilders: SolutionScenario = {
  meta: {
    id: 'home-builders',
    slug: 'home-builders',
    name: 'Home Builders',
    tagline: 'Genies that support homeowners so your customer-care team is not the help desk',
    description: "A genie that gives new homeowners 24/7 how to support, troubleshoots HVAC and smart home issues, and handles warranty calls so your customer care team isn't fielding every question.",
    market: 'Home Builders',
    subMarkets: ['Custom Home Builders', 'Production Builders', 'Residential Contractors', 'Home Warranty Providers', 'New Construction'],
    specificNeeds: [
      'Warranty claim support',
      'Product & appliance information',
      'HVAC troubleshooting',
      'Smart home system help',
      'Maintenance scheduling',
      'Builder communication'
    ],
    commonRoles: ['Warranty Coordinators', 'Customer Care Teams', 'Construction Managers', 'Service Technicians'],
    marketSize: '$450 billion US residential construction market',
    isFeatured: true
  },

  hero: {
    headline: 'A product expert in every home',
    headlineGradient: 'A product expert',
    headlineRest: 'in every home',
    subheadline: "A genie that gives new homeowners 24/7 how to support, troubleshoots HVAC and smart home issues, and handles warranty calls so your customer care team isn't fielding every question.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'equipment-troubleshooting',
    'interactive-faq'
  ],

  applications: [
    {
      id: 'warranty-support',
      name: 'Warranty Claim Support',
      description: 'Guide homeowners through warranty claims and connect them with the right service providers',
      department: 'Customer Care',
      icon: Shield,
      playbookIds: ['interactive-faq', 'appointment-scheduler'],
      benefits: [
        'Streamlined claim submission',
        'Automatic vendor routing',
        'Status updates on demand',
        'Reduced customer frustration'
      ]
    },
    {
      id: 'hvac-support',
      name: 'HVAC System Support',
      description: 'Troubleshoot heating and cooling issues and schedule service appointments',
      department: 'Support',
      icon: Thermometer,
      playbookIds: ['equipment-troubleshooting', 'appointment-scheduler'],
      benefits: [
        'Filter change reminders',
        'Thermostat programming help',
        'Efficiency optimization tips',
        'Emergency service scheduling'
      ]
    },
    {
      id: 'smart-home',
      name: 'Smart Home Assistance',
      description: 'Help homeowners set up and troubleshoot smart home devices and integrations',
      department: 'Support',
      icon: Lightbulb,
      playbookIds: ['equipment-troubleshooting', 'interactive-faq'],
      benefits: [
        'Device setup guidance',
        'Connectivity troubleshooting',
        'Automation programming help',
        'Integration support'
      ]
    },
    {
      id: 'appliance-support',
      name: 'Appliance Information',
      description: 'Provide product information, manuals, and troubleshooting for all installed appliances',
      department: 'Support',
      icon: Package,
      playbookIds: ['interactive-faq', 'equipment-troubleshooting'],
      benefits: [
        'Model-specific guidance',
        'Operating instructions',
        'Maintenance schedules',
        'Warranty registration'
      ]
    },
    {
      id: 'maintenance-scheduling',
      name: 'Maintenance Scheduling',
      description: 'Schedule routine maintenance for HVAC, water heaters, and other home systems',
      department: 'Service',
      icon: Calendar,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Proactive maintenance reminders',
        'Service provider coordination',
        'Seasonal preparation tips',
        'Maintenance history tracking'
      ]
    },
    {
      id: 'new-home-orientation',
      name: 'New Home Orientation',
      description: 'Guide new homeowners through their home features and systems',
      department: 'Sales',
      icon: Home,
      playbookIds: ['interactive-faq'],
      benefits: [
        '24/7 home feature questions',
        'System operation guidance',
        'Product documentation access',
        'Builder contact information'
      ]
    }
  ],

  challenges: [
    {
      title: 'Complex Product Ecosystem',
      description: 'New homes contain dozens of products from different manufacturers, each with unique support needs',
      impact: 'high',
      affectedAreas: ['Customer satisfaction', 'Support costs', 'Warranty management']
    },
    {
      title: 'After Hours Support Demand',
      description: 'Homeowners discover issues and have questions outside business hours',
      impact: 'high',
      affectedAreas: ['Customer experience', 'Staff workload', 'Response times']
    },
    {
      title: 'Warranty Claim Complexity',
      description: 'Homeowners struggle to determine which vendor handles which warranty issue',
      impact: 'high',
      affectedAreas: ['Customer frustration', 'Claim processing time', 'Builder reputation']
    },
    {
      title: 'Homeowner Education Gap',
      description: 'Many homeowners unfamiliar with operating modern home systems and smart technology',
      impact: 'medium',
      affectedAreas: ['Support call volume', 'Product misuse', 'Customer satisfaction']
    }
  ],

  solutions: [
    {
      challenge: 'Complex product ecosystem requiring multiple support channels',
      solution: 'Unified Voice Genie that handles all home systems and routes to appropriate vendors',
      implementation: 'Single point of contact that understands all installed products and their warranty coverage',
      expectedResults: [
        '60% reduction in misrouted support requests',
        'Single phone number for all home support',
        'Instant access to product-specific guidance'
      ]
    },
    {
      challenge: 'After hours support demand',
      solution: '24/7 Voice Genie support with intelligent troubleshooting and scheduling',
      implementation: 'Always on assistance that can troubleshoot issues and schedule service for the next business day',
      expectedResults: [
        'Round-the-clock homeowner support',
        '40% of issues resolved without human intervention',
        'Emergency escalation for critical issues'
      ]
    },
    {
      challenge: 'Homeowner unfamiliarity with modern home technology',
      solution: 'Voice-guided tutorials and troubleshooting for all home systems',
      implementation: 'Interactive voice guidance for thermostats, smart home devices, appliances, and more',
      expectedResults: [
        '50% reduction in how-to support calls',
        'Improved homeowner confidence',
        'Better system utilization'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'BuilderTrend',
        category: 'Construction Management',
        description: 'Construction project management and customer portal',
        required: false,
        popularityScore: 90
      },
      {
        name: 'CoConstruct',
        category: 'Construction Management',
        description: 'Custom home builder management platform',
        required: false,
        popularityScore: 85
      },
      {
        name: 'Lasso CRM',
        category: 'CRM',
        description: 'New home sales and marketing CRM',
        required: false,
        popularityScore: 82
      },
      {
        name: 'BuilderMT',
        category: 'Warranty Management',
        description: 'Home builder warranty management system',
        required: false,
        popularityScore: 78
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Customer relationship management',
        required: false,
        popularityScore: 95
      },
      {
        name: 'ServiceTitan',
        category: 'Field Service',
        description: 'Home service management platform',
        required: false,
        popularityScore: 88
      }
    ]
  },

  metrics: {
    averageImplementationTime: '2-3 weeks',
    typicalROI: '280%',
    adoptionRate: '85% homeowner satisfaction',
    customerSatisfaction: '4.9/5'
  },

  demoConfig: {
    firstMessage: "Hello! I'm your home support assistant. I can help you with questions about your home's systems and appliances, schedule maintenance or warranty service, troubleshoot issues with your HVAC, smart home devices, or any other products in your home. How can I help you today?",
    systemPrompt: "You are a knowledgeable home support assistant for new homeowners. Help them understand and operate their home's systems including HVAC, smart home devices, appliances, and more. Guide them through troubleshooting, schedule service appointments, and assist with warranty claims. Be patient and explain things clearly as many homeowners may be unfamiliar with modern home technology.",
    marketContext: "Home builder or residential construction company providing post-sale homeowner support",
    exampleScenarios: [
      "How do I program my thermostat?",
      "My air conditioner isn't cooling properly",
      "I need to file a warranty claim for my dishwasher",
      "How do I connect my smart home hub?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Summit Custom Homes',
      companySize: '200+ homes per year',
      location: 'Colorado',
      challenge: 'Warranty team overwhelmed with calls about product operation and minor issues that didn\'t require service visits',
      implementation: 'Deployed Help Genie as front line homeowner support for all warranty and product questions',
      timeline: '3 weeks',
      results: [
        {
          metric: 'Warranty Call Reduction',
          value: '55%',
          improvement: 'Fewer calls to warranty team'
        },
        {
          metric: 'Homeowner Satisfaction',
          value: '4.7/5',
          improvement: 'Up from 3.9/5'
        },
        {
          metric: 'Average Response Time',
          value: 'Instant',
          improvement: 'From 4+ hours'
        },
        {
          metric: 'Service Visit Reduction',
          value: '30%',
          improvement: 'Issues resolved by phone'
        }
      ],
      quote: {
        text: "Our homeowners love having instant answers about their homes at any hour. Help Genie handles everything from thermostat questions to warranty routing, and our warranty team can now focus on genuine service issues.",
        author: 'Michael Torres',
        title: 'Director of Customer Care'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Home Builder Voice Support Guide',
      description: 'Implementing voice assistance for new homeowner support',
      url: '/resources/home-builder-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Transforming Homeowner Experience with Voice AI',
      description: 'How leading builders are improving satisfaction and reducing warranty costs',
      url: '/webinars/home-builder-voice-ai'
    },
    {
      type: 'template',
      title: 'Home Systems Troubleshooting Scripts',
      description: 'Pre-built voice scripts for common HVAC, appliance, and smart home issues',
      url: '/templates/home-systems-troubleshooting'
    }
  ],

  faqs: [
    {
      question: 'Can the Genie handle multiple product brands and systems?',
      answer: 'Yes, Help Genie can be configured with information about all the products and systems installed in your homes, regardless of manufacturer. It provides unified support across HVAC, appliances, smart home devices, and more.'
    },
    {
      question: 'How does it route warranty claims to the right vendor?',
      answer: 'The Genie understands your warranty structure and automatically determines which vendor or trade partner handles each type of issue. It can initiate service requests directly with the appropriate party.'
    },
    {
      question: 'Can homeowners use it for both troubleshooting and scheduling?',
      answer: 'Absolutely. The Genie guides homeowners through troubleshooting steps first, and if the issue requires a service visit, it can schedule appointments with your preferred service providers or trade partners.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "New home, new questions\nyour Genie has every answer",
      subtitle: "See how your Genie handles real homeowner calls — resolving warranty issues, troubleshooting systems, and providing support long after the keys are handed over.",
    },
    chatScenarios: [
      {
        id: 'warranty_claim',
        title: 'Warranty Claim',
        messages: [
          { type: 'user' as const, text: "My dishwasher has been leaking from the bottom. We only moved in four months ago — is this covered under warranty?", delay: 300 },
          { type: 'genie' as const, text: "I'm sorry about the leak. Yes — your home is within the one-year builder warranty period, and appliances are typically covered by the manufacturer's warranty as well. Let me pull up your home's details. What's your address?", delay: 1200 },
          { type: 'user' as const, text: "127 Oakridge Drive, lot 42", delay: 2500 },
          { type: 'genie' as const, text: "Found your home — I see you have a Bosch dishwasher installed. This would be covered under Bosch's warranty. I can submit a service request directly to their authorized repair team. What day works for a technician visit?", delay: 3400 },
          { type: 'user' as const, text: "Any morning this week would work", delay: 4600 },
          { type: 'genie' as const, text: "I've submitted a warranty service request to Bosch for 127 Oakridge. They'll contact you within 24 hours to confirm a morning appointment. In the meantime, place a towel under the dishwasher to catch any drips.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Warranty coverage confirmed', delay: 6500 },
          { id: '2', text: 'Home record identified', delay: 6700 },
          { id: '3', text: 'Routed to correct vendor (Bosch)', delay: 6900 },
          { id: '4', text: 'Service request submitted', delay: 7100 },
          { id: '5', text: 'Homeowner guided on interim steps', delay: 7300 },
        ],
      },
      {
        id: 'thermostat_help',
        title: 'Thermostat Help',
        messages: [
          { type: 'user' as const, text: "I can't figure out how to set a schedule on my thermostat. It's the Ecobee that came with the house.", delay: 300 },
          { type: 'genie' as const, text: "No problem — the Ecobee is a great thermostat once you get it set up. On the home screen, tap the menu icon in the bottom left, then tap 'Schedule.' From there, you can set different temperatures for different times of day.", delay: 1200 },
          { type: 'user' as const, text: "Okay I see the schedule screen. How do I set it cooler at night?", delay: 2500 },
          { type: 'genie' as const, text: "Tap on the 'Sleep' comfort setting — you can set your preferred overnight temperature there. Most people set it 3–5 degrees cooler for sleeping. Then set the time it starts — say 10 PM — and when it ends, like 6 AM.", delay: 3400 },
          { type: 'user' as const, text: "Oh that's easy! I set it to 68 at night and 72 during the day. Thanks!", delay: 4600 },
          { type: 'genie' as const, text: "Perfect — those are great settings for comfort and efficiency. Your Ecobee will also learn your patterns over time and optimize automatically. Enjoy your new home!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Product-specific guidance provided', delay: 6500 },
          { id: '2', text: 'Step-by-step walkthrough completed', delay: 6700 },
          { id: '3', text: 'Homeowner self-sufficient now', delay: 6900 },
          { id: '4', text: 'No service visit required', delay: 7100 },
          { id: '5', text: 'Energy efficiency tips shared', delay: 7300 },
        ],
      },
      {
        id: 'hvac_issue',
        title: 'HVAC Troubleshooting',
        messages: [
          { type: 'user' as const, text: "The upstairs is way warmer than downstairs. The AC is running but it's just not reaching the second floor.", delay: 300 },
          { type: 'genie' as const, text: "That's a common concern in two-story homes. Let's check a few things. First, have you checked your air filter recently? A clogged filter is the #1 cause of uneven cooling.", delay: 1200 },
          { type: 'user' as const, text: "I actually haven't checked it since we moved in... where is it?", delay: 2500 },
          { type: 'genie' as const, text: "In your home, the main return filter is typically in the hallway ceiling between the bedrooms. It's behind a hinged grille — just pull it down and slide the filter out. It should be a 20x25 filter. If it looks gray or clogged, that's your culprit.", delay: 3400 },
          { type: 'user' as const, text: "Wow, it's filthy! I had no idea it needed changing.", delay: 4600 },
          { type: 'genie' as const, text: "That'll do it! Replace it with a standard 20x25x1 filter from any hardware store. You should change it every 60–90 days. I'll set up a reminder for you. Your upstairs should start cooling better within an hour.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Root cause identified (dirty filter)', delay: 6500 },
          { id: '2', text: 'Filter location provided for home', delay: 6700 },
          { id: '3', text: 'Issue resolved without service visit', delay: 6900 },
          { id: '4', text: 'Maintenance reminder scheduled', delay: 7100 },
          { id: '5', text: 'Homeowner educated on upkeep', delay: 7300 },
        ],
      },
      {
        id: 'smart_home_setup',
        title: 'Smart Home Setup',
        messages: [
          { type: 'user' as const, text: "I'm trying to connect the smart lock on my front door to my phone but it's not showing up in the app.", delay: 300 },
          { type: 'genie' as const, text: "Let me help with that. Your home comes with a Schlage Encode lock. First, make sure Bluetooth is enabled on your phone. Then open the Schlage Home app — have you downloaded that?", delay: 1200 },
          { type: 'user' as const, text: "Yes I have the app, and Bluetooth is on", delay: 2500 },
          { type: 'genie' as const, text: "Good. Stand within 3 feet of the lock and in the app, tap 'Add Lock.' If it still doesn't appear, try removing the battery pack from the lock for 10 seconds, then reinstall it to reset the Bluetooth.", delay: 3400 },
          { type: 'user' as const, text: "The battery reset worked — it's showing up now!", delay: 4600 },
          { type: 'genie' as const, text: "Excellent! Follow the app prompts to complete pairing. Once connected, you can set up auto-lock, temporary access codes for guests, and even check your lock history. Let me know if you need help with any other smart home features.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Device identified (Schlage Encode)', delay: 6500 },
          { id: '2', text: 'Step-by-step pairing guidance', delay: 6700 },
          { id: '3', text: 'Issue resolved (Bluetooth reset)', delay: 6900 },
          { id: '4', text: 'Feature tips provided', delay: 7100 },
          { id: '5', text: 'No service visit needed', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_hvac',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "It's midnight and our heat just stopped working. It's 30 degrees outside and we have a baby in the house.", delay: 300 },
          { type: 'genie' as const, text: "I understand — with a baby at home, this is urgent. Let's check one quick thing: is your thermostat displaying anything? Sometimes a simple reset fixes it — switch it to 'Off,' wait 30 seconds, then switch back to 'Heat.'", delay: 1200 },
          { type: 'user' as const, text: "I tried that, it just says 'waiting' and nothing happens", delay: 2500 },
          { type: 'genie' as const, text: "That 'waiting' message means the thermostat is trying to communicate with the furnace. Check your breaker panel — is the furnace breaker tripped?", delay: 3400 },
          { type: 'user' as const, text: "The breaker looks fine. Still no heat.", delay: 4600 },
          { type: 'genie' as const, text: "I'm going to escalate this as an emergency. I've flagged your home at 127 Oakridge for priority HVAC service. You'll receive a call within 30 minutes. In the meantime, use space heaters and keep the baby's room warm. Help is on the way.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Emergency severity assessed (baby)', delay: 6500 },
          { id: '2', text: 'Remote troubleshooting attempted', delay: 6700 },
          { id: '3', text: 'Emergency escalation triggered', delay: 6900 },
          { id: '4', text: 'Priority callback within 30 min', delay: 7100 },
          { id: '5', text: 'Safety guidance provided', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Support That Builds Trust",
      subtitle: "Your reputation lives on long after the final walkthrough. Your Genie gives homeowners instant answers about every system in their new home.",
    },
    useCaseCards: [
      {
        id: 'warranty-routing',
        title: 'Warranty Claim Routing',
        description: "Homeowners don't know if it's the builder's warranty or the manufacturer's. Your Genie figures it out and routes to the right vendor automatically.",
        bullets: [
          'Identifies warranty coverage by product',
          'Routes to correct vendor or trade partner',
          'Submits service requests directly',
          'Tracks claim status for homeowners',
        ],
        image: '/solutions-pages/home-builders/usecase-warranty-routing.webp',
      },
      {
        id: 'system-troubleshooting',
        title: 'Home System Troubleshooting',
        description: "HVAC not cooling, thermostat confusion, water heater questions — your Genie walks homeowners through fixes before you send a truck.",
        bullets: [
          'Filter change guidance and reminders',
          'Thermostat programming help',
          'Product-specific troubleshooting steps',
          'Resolves 40%+ of calls without a visit',
        ],
        image: '/solutions-pages/home-builders/usecase-system-troubleshooting.webp',
      },
      {
        id: 'smart-home-support',
        title: 'Smart Home Assistance',
        description: "Smart locks, thermostats, cameras, garage doors — your Genie helps homeowners set up and use the tech that came with their home.",
        bullets: [
          'Device pairing and setup guidance',
          'App and connectivity troubleshooting',
          'Automation programming help',
          'Reduces callback and support volume',
        ],
        image: '/solutions-pages/home-builders/usecase-smart-home.webp',
      },
      {
        id: 'new-home-orientation',
        title: 'New Home Orientation',
        description: "Homeowners forget 80% of what they learn at orientation. Your Genie is a 24/7 resource for every system, product, and feature in their home.",
        bullets: [
          'Product manuals and guides on demand',
          'Operating instructions for every system',
          'Maintenance schedules and reminders',
          'Builder contact info when needed',
        ],
        image: '/solutions-pages/home-builders/usecase-home-orientation.webp',
      },
    ],
    finalCTA: {
      title: "Your Homes Come With a Genie",
      subtitle: "Long after the keys are handed over, your Genie supports every homeowner — answering questions, resolving issues, and protecting your builder reputation.",
      pillars: [
        {
          title: 'Always\nSupporting',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Warranty claims routed, systems troubleshot, and questions answered — 24/7.',
        },
        {
          title: 'Always\nTeaching',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Homeowners learn their systems with guided setup, tips, and reminders.',
        },
        {
          title: 'Always\nProtecting',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Your brand reputation stays strong with world-class post-sale support.',
        },
      ],
      backgroundImage: '/solutions-pages/home-builders/cta-background.webp',
    },
  }
};
